import {
  Dates,
  Invoice,
  Translations,
  BicHelper,
  InvoiceType,
  Arrays,
  InvoiceStatus,
  PaymentChannel,
  Numeric,
  Barcodes,
  QrCodes,
  Iban
} from "@salaxy/core";
import { HeaderFooterData, ReportOptions, ReportLayout } from "../../model";

/** Type used to present InvoiceMappers getInvoice() result */
export type MappedInvoice = {
  /** Containing header related data */
  header?: {
    /** Date of invoice */
    date: string | null;
    /** Due date of invoice */
    dueDate: string | null;
    /** Payment reference (e.g. RF0710010831). Please note that in salary payment scenarios, this is not necessarily unique. */
    referenceNumber: string | null;
    /** The free text that is displayed / sent with the invoice. May have multiple lines. */
    message?: string | null;
  };
  /** Containing total related data */
  totals?: {
    /** Total amount without vat */
    totalExVat: number | null;
    /** Total amount of vat */
    totalVat: number | null;
    /** Total amount with vat */
    total: number | null;
    /** Total amount of social security tax */
    taxSocialSecurity: number | null;
    /** Total amount of withholding tax */
    taxWithholding: number | null;
    /** Invoice total paid */
    totalPaid?: number | null;
    /** Invoice total remaining */
    toBePaid?: number | null;
    /** Helper for hbs template to show totalPaid and toBePaid */
    showTotalPaid?: boolean | null;
  };
  /** Containing row related data */
  rows?: {
    /** Message (RowFreeText in Finvoice) is the message is mainly designed for accounting systems. */
    message: string | null;
    /** Name of the product or service. In the case of Salaxy salaries, this is language versioned text of CalculationRowType. */
    articleName: string | null;
    /** Count (DeliveredQuantity in Finvoice) is the quantity of product or service. */
    count: number | null;
    /** Price including VAT (UnitPriceVatIncludedAmount, Yksikköhinta verollinen) */
    price: number | null;
    /** Price of one product or service without Vat (UnitPriceAmount, Yksikköhinta veroton). Yksikköhinta voidaan ilmoittaa myös verollisena UnitPriceVatIncludedAmount- elementissä laskutuskäytännön ja verottajan ALV-ohjeistuksen mukaan. */
    priceExVat: number | null;
    /** Row total with VAT (RowAmount, Yhteensä verollinen) */
    total: number | null;
    /** Row total excluding VAT (RowVatExcludedAmount, Yhteensä veroton) */
    totalExVat: number | null;
    /** Vat percent related to the product or service (RowVatRatePercent, Alv%) */
    vatPercent: number | null;
    /** Suggested account number (RowNormalProposedAccountIdentifier). */
    accountingAccountNumber: string | null;
    /** Textual code for accounting mapping (RowProposedAccountText, Raportointikoodi). In Finvoice: "Accounting information agreed between seller and buyer." */
    accountingCode: string | null;
  }[];
  /** Is this a preview invoice */
  isPreview?: boolean | null;
  /** Containing payer related data */
  payer?: {
    /** Name of payer */
    name: string | null;
    /** Official identifier of the party: Finnish HETU (Soc. sec. number) for a Person or Y-tunnus (Company ID for a company or association). */
    officialId: string | null;
    /** Street of payer */
    street: string;
    /** Postal code of payer */
    postalCode: string;
    /** City of payer */
    city: string;
  };
  /** Containing recipient related data */
  recipient?: {
    /** Name of recipient */
    name: string | null;
    /**
     * This is an official identifier of the party: Finnish Personal Id (HETU) for a Person or Company Id (Y-tunnus) for a company or association.
     * WARNING: This is currently needed for Finvoice only (https://www.finanssiala.fi/finvoice/Sivut/default.aspx). As such we consider removing it from persons.
     * Please inform if you use this property!
     */
    officialId: string | null;
    /** Street of recipient */
    street: string | null;
    /** Postal code of recipient */
    postalCode: string | null;
    /** City of recipient */
    city: string | null;
    /** The International Bank Account Number of the receiver. */
    iban: string | null;
    /** Bic of recipient */
    bic: string | null;
  };
  /** Barcode string, svg */
  barcode?: string | null;
  /** QrCode, data url */
  qrCode?: string | null;
};

/**
 * Mapper for Invoice.
 */
export class InvoiceMapper {
  /**
   * Loads report type specific HeaderFooterData.
   * @param businessObject The Invoice entity used to load the HeaderFooterData.
   * @param reportOptions The ReportOptions entity used to load the HeaderFooterData.
   * @param lang gets translation with this lang, if null use current lang
   * @returns Returns HeaderFooterData.
   */
  public static getHeaderFooter(
    businessObject: Invoice,
    reportOptions: ReportOptions,
    lang: string = null
  ): HeaderFooterData {
    const headerFooter: HeaderFooterData = {
      logo: reportOptions?.logo,
      img2: reportOptions?.img2,
      title: Translations.get("SALAXY.REPORTS.invoice.title", null, lang),
      subTitle: Translations.get(
        "SALAXY.ENUM.InvoiceType." + businessObject.header.type + ".label"
      ),
      today: Dates.getFormattedDate(Dates.getToday()),
      date: Dates.getFormattedDate(Dates.getToday()),
    };
    return headerFooter;
  }

  /**
   * Loads the raw report that is then rendered downstream as an html or pdf report.
   * @param businessObject Invoice entity
   * @returns Authorization
   */
  public static async getInvoice(businessObject: Invoice, getDocumentAndSvgNode: () => any): Promise<MappedInvoice> {
    const harmonizeVat = (vat: number): number => {
      if (vat <= 1) {
        return vat;
      }
      return vat / 100.0;
    };
    const result: MappedInvoice = {
      header: {
        date: Dates.getFormattedDate(businessObject.header.date),
        dueDate: Dates.getFormattedDate(businessObject.header.dueDate),
        referenceNumber: this.getReferenceNumber(businessObject),
        message: businessObject.header.message,
      },
      totals: {
        totalExVat: businessObject.header.totalExVat,
        totalVat:
          businessObject.header.total - businessObject.header.totalExVat,
        total: businessObject.header.total,
        taxSocialSecurity: Arrays.sum(
          businessObject.rows.filter(
            (x) => x.articleId === InvoiceType.TaxSocialSecurity
          ),
          (x) => x.total
        ),
        taxWithholding: Arrays.sum(
          businessObject.rows.filter(
            (x) => x.articleId === InvoiceType.TaxWithholding
          ),
          (x) => x.total
        ),
        totalPaid: (businessObject.header as any)?.totalPaid,
        toBePaid: businessObject.header.total - (businessObject.header as any)?.totalPaid,
        showTotalPaid: (businessObject.header as any)?.totalPaid && (businessObject.header as any)?.totalPaid != businessObject.header.total,
      },
      rows: [],
      isPreview: false,
      payer: {
        name: businessObject.payer.avatar.displayName,
        officialId: businessObject.payer.officialId,
        street: businessObject.payer.contact.street,
        postalCode: businessObject.payer.contact.postalCode,
        city: businessObject.payer.contact.city,
      },
      recipient: {
        name: businessObject.recipient.fullName,
        officialId: businessObject.header.type === InvoiceType.Net ? "" :businessObject.recipient.officialId,
        street: businessObject.recipient.contact.street,
        postalCode: businessObject.recipient.contact.postalCode,
        city: businessObject.recipient.contact.city,
        iban: businessObject.recipient.iban,
        bic: BicHelper.getBicForIban(businessObject.recipient.iban)
      },
      barcode: !Iban.isInternationalIban(businessObject.recipient.iban ?? "") ? this.createBarCode(getDocumentAndSvgNode, businessObject.recipient.iban ?? "", this.getReferenceNumber(businessObject), Dates.asDate(businessObject.header.dueDate), Numeric.round(this.getPayment(businessObject) * 100, 0)) : null,
      qrCode: await this.createQrCode(businessObject.recipient.iban ?? "", this.getReferenceNumber(businessObject), Dates.asDate(businessObject.header.dueDate), Numeric.round(this.getPayment(businessObject)), businessObject.recipient.fullName)

    };
    businessObject.rows.forEach((r) => {
      const row: any = {};
      row.message = r.message;
      row.articleName = r.articleName;
      row.count = r.count;
      row.price = r.price;
      row.priceExVat = r.priceExVat;
      row.total = r.total;
      row.totalExVat = r.totalExVat;
      row.vatPercent = harmonizeVat(r.vatPercent ?? 0);
      row.accountingAccountNumber = r.accountingAccountNumber;
      row.accountingCode = r.accountingCode;
      result.rows.push(row);
    });
    if (this.isPreviewInvoice(businessObject)) {
      result.recipient.iban = Translations.get(
        "SALAXY.REPORTS.invoice.heading.preview"
      );
      result.recipient.bic = Translations.get(
        "SALAXY.REPORTS.invoice.heading.preview"
      );
      result.isPreview = true;
    }
    return result;
  }

  private static getReferenceNumber(businessObject: Invoice): string {
    return businessObject.header.type === InvoiceType.Net && businessObject.header.channel === PaymentChannel.PalkkausInvoices ? "" : businessObject.header.referenceNumber ?? "";
  }

  private static getPayment(businessObject: Invoice): number {
    if ((businessObject.header as any)?.totalPaid && (businessObject.header as any)?.totalPaid != businessObject.header.total) {
      return businessObject.header.total - (businessObject.header as any)?.totalPaid;
    }

    return businessObject.header.total;
  }

  private static createBarCode(getDocumentAndSvgNode: () => any, iban: string, ref: string, dueDate: string, euroAmount: number): string {
    const regex = /^(?:RF\d+|\d+)$/i;
    // check if ref starts with rf (case sensitive) and then contains numbers or if ref contains only numbers
    if(!regex.test(ref)) {
      return "";
    }

    try {
      return Barcodes.createBarcode(getDocumentAndSvgNode, iban, ref, dueDate, euroAmount, {width: 1.5, height: 20, displayValue: true, fontSize: 15});
    } catch(e) {
      console.error(e);
      return "";
    }
  }

  private static async createQrCode(iban: string, ref: string, dueDate: string, euroAmount: number, recipientName: string): Promise<string> {
    const regex = /^(?:RF\d+|\d+)$/i;
    // check if ref starts with rf (case sensitive) and then contains numbers or if ref contains only numbers
    if(!regex.test(ref)) {
      return "";
    }

    try {
      return await QrCodes.getValue(BicHelper.getBicForIban(iban), recipientName, iban, ref, null, euroAmount, dueDate);
    } catch(e) {
      console.error(e);
      return "";
    }
  }

  private static isPreviewInvoice(invoice: Invoice): boolean {
    if(invoice.header.channel === PaymentChannel.PalkkausInvoices) {
      return false;
    }
    return (
      [InvoiceType.Pension, InvoiceType.Unemployment].includes(
        invoice.header.type
      ) &&
      [InvoiceStatus.Preview, InvoiceStatus.Forecast].includes(
        invoice.header.status
      )
    );
  }

  /**
   * Loads the invoice layout for pdf or html reports.
   * @param pdf Are we rendering a pdf report or not.
   * @returns Returns invoice layout.
   */
  public static loadReportLayout(pdf: boolean): ReportLayout {
    const reportLayout: ReportLayout = {
      isLayoutA4Portrait: true,
      isLayoutA4Landscape: false,
      layout: "a4Portrait",
      date: Dates.getFormattedDate(Dates.getToday()),
      isPreview: false,
      mode: pdf ? "pdf" : "htmlPage",
      isModeHtmlPage: !pdf,
      isModePdf: pdf,
    };
    return reportLayout;
  }

  /**
   * Loads Invoice specific ReportOptions
   * @returns ReportOptions for report
   */
  public static loadReportOptions(businessObject: Invoice): ReportOptions {
    // If InvoiceType is gross return palkkaus template otherwise use normal invoice template
    if (businessObject.header.type === InvoiceType.Gross) {
      return {
        css: null,
        printBackground: false,
        margin: {
          top: 30,
          bottom: 30,
          left: 10,
          right: 10,
        },
        headerTemplate: `
        <div style=" color:#808080; margin:10mm 10mm 0 10mm; font-family: Helvetica, Arial, sans-serif;">
          <table style="width: 100%; border-bottom: 0.75mm solid #009fd8;">
            <tr>
              <td width="40%">{{> logo }}</td>
              <td width="60%" style="font-weight: bold">
                <div style="font-size: 1.3em">{{ title }}</div>
                <div style="font-size: 1.1em;padding-bottom: 2mm">{{ subTitle }}</div>
              </td>
            </tr>
          </table>
        </div>
        `,
        footerTemplate: `
          <div style="margin:5mm 10mm 0 10mm; padding-top: 3mm; border-top: 0.75mm solid #009fd8;font-family: Helvetica, Arial, sans-serif;">
          {{t "SALAXY.REPORTS.document.footerInfo.html" date=( today) }}
            <div style="position: absolute;top: 8mm; right: 10mm">
            {{> pageNumber }} / {{> totalPages }}
            </div>
          </div>
        `,
        logo: `<svg width="175px" height="40px" viewBox="0 0 668 152" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
        <title>Palkkaus.fi</title>
        <g id="Palkkaus_logo_rgb" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
            <g>
                <path d="M495.195,118.538 C493.118,118.538 491.43,116.85 491.43,114.775 L491.43,53.743 C491.43,51.701 493.154,49.976 495.195,49.976 C497.296,49.976 499.071,51.701 499.071,53.743 L499.071,114.775 C499.071,116.886 497.368,118.538 495.195,118.538 Z M495.195,44.781 C493.118,44.781 491.43,43.092 491.43,41.016 L491.43,39.384 C491.43,37.344 493.154,35.62 495.195,35.62 C497.296,35.62 499.071,37.344 499.071,39.384 L499.071,41.016 C499.071,43.127 497.368,44.781 495.195,44.781 Z" id="Fill-5" fill="#2F3231"></path>
                <path d="M476.738,57.194 L468.645,57.194 L468.626,51.982 C468.626,45.466 469.175,43.165 470.711,43.165 C472.884,43.165 474.587,41.512 474.587,39.401 C474.587,37.289 472.884,35.635 470.711,35.635 C468.267,35.635 462.462,36.836 461.325,47.949 L461.321,47.991 L461.318,48.032 L461.097,52.226 L461.096,52.27 L461.096,57.194 L453.056,57.194 C451.014,57.194 449.289,58.968 449.289,61.07 C449.289,63.111 451.014,64.836 453.056,64.836 L461.096,64.836 L461.096,113.67 C461.096,115.711 462.82,117.436 464.861,117.436 C466.963,117.436 468.737,115.711 468.737,113.667 L468.66,64.836 L476.738,64.836 C478.814,64.836 480.502,63.148 480.502,61.07 C480.502,58.897 478.848,57.194 476.738,57.194" id="Fill-13" fill="#2F3231"></path>
                <path d="M444.976,117.415 C442.899,117.415 441.21,115.727 441.21,113.649 L441.21,112.019 C441.21,109.977 442.934,108.253 444.976,108.253 C447.077,108.253 448.851,109.977 448.851,112.019 L448.851,113.649 C448.851,115.761 447.149,117.415 444.976,117.415" id="Fill-7" fill="#2F3231"></path>
                <path d="M407.851,117.989 C390.358,117.989 384.949,107.462 383.281,101.186 C383.007,100.271 383.121,99.247 383.616,98.356 C384.126,97.437 384.972,96.78 386.001,96.499 L386.111,96.473 C386.36,96.423 386.614,96.398 386.866,96.398 C388.56,96.398 390.086,97.541 390.577,99.175 C392.606,106.783 398.251,110.456 407.851,110.456 C419.706,110.456 425.717,106.155 425.717,97.669 C425.717,87.376 413.67,86.319 413.548,86.312 L413.509,86.31 L413.43,86.305 L402.062,85.202 C395.571,84.722 383.225,80.546 383.225,68.2 C383.225,60.816 386.525,48.429 408.622,48.429 C418.997,48.429 426.382,52.003 430.577,59.052 C431.685,60.782 431.172,63.12 429.415,64.292 L429.298,64.362 C428.739,64.672 428.1,64.836 427.449,64.836 C426.109,64.836 424.865,64.171 424.12,63.056 L424.097,63.02 L424.075,62.985 C421.304,58.334 416.249,56.073 408.622,56.073 C392.61,56.073 390.758,62.994 390.758,68.2 C390.758,76.838 402.466,77.675 402.584,77.68 L402.624,77.681 L402.698,77.686 L414.065,78.679 C420.702,79.271 433.25,84.002 433.25,97.669 C433.25,103.779 430.775,117.989 407.851,117.989" id="Fill-12" fill="#2F3231"></path>
                <path d="M346.699,118.097 C324.513,118.097 321.523,102.581 321.523,87.404 L321.523,53.742 C321.523,51.594 323.14,49.976 325.288,49.976 C327.365,49.976 329.052,51.666 329.052,53.742 L329.052,87.404 C329.052,102.427 331.866,110.568 346.699,110.568 C362.311,110.568 365.449,101.704 365.449,87.404 L365.449,53.742 C365.449,51.594 367.066,49.976 369.213,49.976 C371.361,49.976 372.979,51.594 372.979,53.742 L372.979,87.404 C372.979,101.694 369.992,118.097 346.699,118.097" id="Fill-9" fill="#2F3231"></path>
                <path d="M284.47,57.398 C269.637,57.398 266.823,65.539 266.823,80.561 L266.823,86.632 C266.823,101.724 269.671,109.904 284.691,109.904 C297.773,109.904 303.109,104.025 303.109,89.612 L303.219,80.54 C303.289,71.105 301.763,65.326 298.272,61.808 C295.327,58.839 290.811,57.398 284.47,57.398 Z M306.874,117.436 C304.834,117.436 303.109,115.711 303.109,113.671 L303.109,111.479 C298.869,115.479 292.796,117.436 284.691,117.436 C262.307,117.436 259.289,101.863 259.289,86.632 L259.289,80.561 C259.289,65.328 262.282,49.754 284.47,49.754 C292.9,49.754 299.366,51.964 303.686,56.318 C310.063,62.742 310.811,72.654 310.752,80.574 L310.752,113.671 C310.752,115.711 308.975,117.436 306.874,117.436 Z" id="Fill-8" fill="#2F3231"></path>
                <path d="M209.297,117.436 C207.221,117.436 205.531,115.747 205.531,113.671 L205.531,39.37 C205.531,37.329 207.257,35.605 209.297,35.605 C211.398,35.605 213.175,37.329 213.175,39.37 L213.175,79.607 L219.828,79.607 C219.833,79.607 222.523,79.615 224.091,79.615 C239.712,79.598 242.84,70.741 242.84,56.454 L242.84,53.742 C242.84,51.594 244.46,49.974 246.605,49.974 C248.752,49.974 250.371,51.594 250.371,53.742 L250.371,56.454 C250.371,66.101 249.093,77.586 239.787,83.382 C249.093,89.177 250.371,100.662 250.371,110.309 L250.371,113.656 C250.371,115.803 248.752,117.422 246.605,117.422 C244.46,117.422 242.84,115.803 242.84,113.656 L242.84,110.309 C242.84,96.022 239.712,87.163 224.135,87.148 C222.523,87.149 219.833,87.157 219.833,87.157 L213.175,87.157 L213.175,113.671 C213.175,115.783 211.471,117.436 209.297,117.436" id="Fill-11" fill="#2F3231"></path>
                <path d="M152.456,117.436 C150.379,117.436 148.689,115.747 148.689,113.671 L148.689,39.37 C148.689,37.329 150.414,35.605 152.456,35.605 C154.557,35.605 156.332,37.329 156.332,39.37 L156.332,79.607 L162.986,79.607 C162.99,79.607 165.68,79.615 167.248,79.615 C182.869,79.598 185.998,70.741 185.998,56.454 L185.998,53.742 C185.998,51.594 187.617,49.974 189.763,49.974 C191.909,49.974 193.528,51.594 193.528,53.742 L193.528,56.454 C193.528,66.101 192.251,77.586 182.944,83.382 C192.251,89.177 193.528,100.662 193.528,110.309 L193.528,113.656 C193.528,115.803 191.909,117.422 189.763,117.422 C187.617,117.422 185.998,115.803 185.998,113.656 L185.998,110.309 C185.998,96.022 182.869,87.163 167.292,87.148 C165.68,87.149 162.99,87.157 162.99,87.157 L156.332,87.157 L156.332,113.671 C156.332,115.783 154.628,117.436 152.456,117.436" id="Fill-10" fill="#2F3231"></path>
                <path d="M131.935,101.06 C131.935,107.575 132.484,109.876 134.02,109.876 C136.192,109.876 137.896,111.529 137.896,113.64 C137.896,115.752 136.192,117.407 134.02,117.407 C131.576,117.407 125.771,116.205 124.634,105.092 L124.63,105.05 L124.626,105.009 L124.406,100.816 L124.404,100.771 L124.404,39.372 C124.404,37.33 126.129,35.605 128.17,35.605 C130.271,35.605 132.046,37.33 132.046,39.374 L131.935,101.06" id="Fill-14" fill="#2F3231"></path>
                <path d="M85.293,57.398 C70.461,57.398 67.649,65.539 67.649,80.561 L67.649,86.632 C67.649,101.724 70.495,109.904 85.515,109.904 C98.597,109.904 103.932,104.025 103.932,89.612 L104.043,80.54 C104.113,71.105 102.587,65.326 99.096,61.808 C96.15,58.839 91.635,57.398 85.293,57.398 Z M107.699,117.436 C105.657,117.436 103.932,115.711 103.932,113.671 L103.932,111.479 C99.693,115.479 93.619,117.436 85.515,117.436 C63.134,117.436 60.116,101.863 60.116,86.632 L60.116,80.561 C60.116,65.328 63.109,49.754 85.293,49.754 C93.724,49.754 100.19,51.964 104.51,56.318 C110.886,62.74 111.634,72.654 111.575,80.574 L111.575,113.671 C111.575,115.711 109.8,117.436 107.699,117.436 Z" id="Fill-4" fill="#2F3231"></path>
                <path d="M25.066,56.625 C10.325,56.625 7.532,64.727 7.532,79.677 L7.532,86.961 C7.532,101.985 10.361,110.126 25.287,110.126 C40.771,110.126 43.706,101.985 43.706,86.961 L43.706,79.677 C43.706,65.077 40.657,56.625 25.066,56.625 Z M3.766,132.867 C1.725,132.867 0,131.142 0,129.101 L0,79.677 C0,64.553 2.979,49.094 25.066,49.094 C48.361,49.094 51.348,65.438 51.348,79.677 L51.348,86.961 C51.348,101.253 48.385,117.658 25.287,117.658 C17.558,117.658 11.702,115.774 7.532,111.923 L7.532,129.101 C7.532,131.178 5.843,132.867 3.766,132.867 Z" id="Fill-1" fill="#2F3231"></path>
                <g id="GraphicLogo" transform="translate(527.000000, 0.000000)">
                    <path d="M18.633,90.281 C13.09,93.033 8.269,97.393 4.944,103.157 C-4.049,118.726 1.281,138.638 16.858,147.632 C21.986,150.593 27.584,152 33.108,152 C44.362,152 55.308,146.158 61.34,135.712 C64.677,129.929 66.038,123.545 65.635,117.345 C63.095,117.821 60.537,118.056 57.991,118.056 C43.576,118.056 29.556,110.574 21.831,97.19 C20.535,94.947 19.472,92.636 18.633,90.281" id="Fill-15" fill="#BF196F"></path>
                    <path d="M107.683,43.881 C101.036,43.881 94.859,45.87 89.707,49.285 C91.319,51.182 92.784,53.25 94.073,55.483 C103.167,71.241 100.814,90.458 89.598,103.519 C94.771,106.98 100.989,108.997 107.683,108.997 C125.662,108.997 140.237,94.421 140.237,76.443 C140.237,58.457 125.662,43.881 107.683,43.881" id="Fill-16" fill="#1795C5"></path>
                    <path d="M65.576,35.334 C64.902,45.837 59.157,55.819 49.353,61.475 C44.228,64.433 38.633,65.839 33.111,65.839 C28.084,65.839 23.116,64.672 18.62,62.452 C15.508,71.278 15.366,81.102 18.633,90.28 C23.139,88.042 28.124,86.869 33.169,86.868 C38.693,86.868 44.291,88.275 49.419,91.237 C59.208,96.889 64.951,106.855 65.634,117.346 C70.149,116.5 74.6,114.887 78.802,112.461 C82.995,110.038 86.611,106.996 89.598,103.519 C80.868,97.68 75.119,87.732 75.119,76.443 C75.119,65.102 80.916,55.116 89.708,49.285 C83.386,41.848 74.793,37.05 65.576,35.334" id="Fill-17" fill="#82BE44"></path>
                    <path d="M33.169,86.868 C28.124,86.868 23.139,88.042 18.633,90.281 C19.472,92.635 20.535,94.948 21.831,97.19 C29.556,110.574 43.576,118.055 57.991,118.055 C60.537,118.055 63.096,117.821 65.635,117.345 C64.951,106.855 59.208,96.889 49.419,91.237 C44.291,88.275 38.693,86.868 33.169,86.868" id="Fill-18" fill="#66292B"></path>
                    <path d="M89.707,49.286 C80.916,55.116 75.119,65.101 75.119,76.442 C75.119,87.732 80.868,97.679 89.598,103.519 C100.814,90.458 103.167,71.241 94.073,55.482 C92.784,53.25 91.319,51.182 89.707,49.286" id="Fill-19" fill="#2C9250"></path>
                    <path d="M33.05,0.712 C27.527,0.712 21.925,2.12 16.798,5.08 C1.222,14.074 -4.109,33.981 4.877,49.555 C8.216,55.333 13.054,59.701 18.62,62.452 C21.812,53.397 28.13,45.395 37.095,40.219 C43.665,36.427 50.837,34.626 57.912,34.624 C60.492,34.624 63.057,34.864 65.576,35.334 C65.972,29.141 64.608,22.771 61.274,16.993 C55.247,6.55 44.303,0.711 33.05,0.712" id="Fill-21" fill="#EA893F"></path>
                    <path d="M57.913,34.624 C50.837,34.626 43.665,36.427 37.095,40.219 C28.13,45.395 21.812,53.398 18.62,62.452 C23.116,64.672 28.084,65.839 33.111,65.839 C38.633,65.839 44.228,64.433 49.353,61.475 C59.157,55.818 64.903,45.837 65.576,35.334 C63.057,34.864 60.492,34.624 57.913,34.624" id="Fill-23" fill="#55663D"></path>
                </g>
            </g>
        </g>
      </svg>`,
      };
    } else {
      return {
        css: "",
        printBackground: false,
        margin: {
          top: 30,
          bottom: 20,
          left: 10,
          right: 10,
        },
        headerTemplate: `
      <div style="color:#808080; margin:10mm 10mm 0 10mm;font-family: Helvetica, Arial, sans-serif;">
      <table style="width: 100%; border-bottom: 0.75mm solid #000;">
        <tr align="center">
          <td style="font-weight: bold; text-align: center">
            <div style="font-size: 1.3em">{{ title }}</div>
            <div style="font-size: 1.1em;padding-bottom: 2mm">{{ subTitle }}</div>
          </td>
        </tr>
      </table>
    </div>
  `,
        footerTemplate:
          businessObject.header.channel === PaymentChannel.Askelma
            ? `
          <div style="margin:0mm 10mm 0 10mm; padding-top: 3mm; border-top: 0.75mm solid #000;font-family: Helvetica, Arial, sans-serif;">
            <div style="position: absolute;top: 3mm; right: 10mm">
            {{> pageNumber }} / {{> totalPages }}
            </div>
          </div>
        `
            : `
          <div style="margin:0mm 10mm 0 10mm; padding-top: 3mm; border-top: 0.75mm solid #000;font-family: Helvetica, Arial, sans-serif;">
            {{t "SALAXY.REPORTS.invoice.footer.html" date=( today) }}
            <div style="position: absolute;top: 3mm; right: 10mm">
              {{> pageNumber }} / {{> totalPages }}
            </div>
          </div>
        `,
        logo: "",
      };
    }
  }
}
